export function esc(str: unknown): string {
  const el = document.createElement('span');
  el.textContent = str == null ? '' : String(str);
  return el.innerHTML;
}

export function csvVal(str: unknown): string {
  if (str == null) return '';
  const s = String(str);
  return s.includes(',') || s.includes('"') || s.includes('\n')
    ? `"${s.replace(/"/g, '""')}"`
    : s;
}

export function fmtTs(unix: number | null | undefined): string {
  if (unix == null) return '—';
  return fmtDate(new Date(unix * 1000));
}

export function fmtDate(d: Date | null | undefined): string {
  if (!d) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function isGroupLast<T, K extends keyof T>(rows: T[], index: number, key: K): boolean {
  if (index >= rows.length - 1) return true;
  return rows[index]?.[key] !== rows[index + 1]?.[key];
}

export function formatHeader(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export type ClientSummaryOptions<T> = {
  peopleAccessor?: (r: T) => number;
};

export function clientSummaryHtml<T extends { client_slug: string }>(
  filtered: T[],
  options: ClientSummaryOptions<T> = {},
): string {
  const { peopleAccessor } = options;
  const counts = new Map<string, number>();
  const peopleCounts = new Map<string, number>();
  for (const r of filtered) {
    const slug = r.client_slug;
    counts.set(slug, (counts.get(slug) ?? 0) + 1);
    if (peopleAccessor) {
      peopleCounts.set(slug, (peopleCounts.get(slug) ?? 0) + (peopleAccessor(r) || 0));
    }
  }
  if (counts.size === 0) return '';
  const chips = [...counts.entries()]
    .map(([slug, n]) => {
      const peoplePart = peopleAccessor
        ? ` <span class="chip-people">(${(peopleCounts.get(slug) ?? 0).toLocaleString()} people)</span>`
        : '';
      return `<span class="summary-chip">${esc(slug)}: ${n}${peoplePart}</span>`;
    })
    .join('');
  return `<div class="client-summary">${chips}</div>`;
}

export function padFn(s: string, w: number, right: boolean): string {
  return right ? s.padStart(w) : s.padEnd(w);
}

export async function writeClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // fall through to legacy path
    }
  }
  // Legacy fallback for non-secure contexts (e.g. http://10.x.x.x).
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } finally {
    document.body.removeChild(ta);
  }
  if (!ok) throw new Error('clipboard write failed');
}

export function downloadCsv(csv: string, reportType: string): void {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${reportType}-report-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revocation so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
